const React = require('react')
const { useState, useEffect } = require('react')
const { useHistory, useLocation } = require('react-router-dom')
const {
    Container,
    Col,
    Row,
    Input,
    Button,
    Dropdown,
    DropdownToggle,
    DropdownMenu,
    DropdownItem,
    Table,
    UncontrolledTooltip
} = require('reactstrap')
const { TableHeader, TableFooter } = require('@components/table')
const { useBlockchainData, useResponsive, BREAKPOINTS } = require('@hooks')
const { useNetworkConfig } = require('@contexts')
const { LoadingSpinner, CopyButton } = require('@components')
const { NETWORK_CONFIG } = require('@configs')

const SimilarContracts = () => {
    const history = useHistory()
    const { network } = useNetworkConfig()
    const { getSimilarContracts } = useBlockchainData(network.name)
    const location = useLocation()
    const params = new URLSearchParams(location.search)

    const isMobile = useResponsive(BREAKPOINTS.large)

    const [contractAddress, setContractAddress] = useState(params.get('a') || '')
    const [page, setPage] = useState(Number(params.get('p')) || 1)
    const [limit, setLimit] = useState(Number(params.get('ps')) || 25)

    const [similarContractsData, setSimilarContractsData] = useState()

    const [loading, setLoading] = useState(false)
    const [fetching, setFetching] = useState(false)

    const [error, setError] = useState()
    const [dropdownOpen, setDropdownOpen] = useState(false)
    const [selectedChain, setSelectedChain] = useState([network.name])

    const SUPPORTED_CHAINS = Object.values(NETWORK_CONFIG).map(chain => ({
        value: chain.shortName,
        label: chain.name,
        icon: chain.networkIcon
    }))

    const handleAddressChange = e => {
        const value = e.target.value
        setContractAddress(value)

        if (value.trim() === '') {
            setError('Required')
        } else if (value.length !== 42) {
            setError('Invalid length')
        } else {
            setError('')
        }
    }

    const fetchData = async ({ page, limit }) => {
        setFetching(true)
        setLoading(true)
        try {
            const [similarContractsResponse] = await Promise.all([getSimilarContracts(page, limit, contractAddress)])

            if (similarContractsResponse) {
                setSimilarContractsData(similarContractsResponse)
            }
        } catch (error) {
            console.error('Error fetching data:', error)
        } finally {
            setLoading(false)
        }
    }

    const handleSearch = () => {
        if (
            !contractAddress ||
            contractAddress.trim() === '' ||
            contractAddress.length !== 42 ||
            !contractAddress.startsWith('0x')
        ) {
            setError('Invalid contract address.')
            return
        }
        if (selectedChain.length === 0) {
            setSelectedChain([network.name])
            return
        }
        setError('')
        fetchData({ page, limit })
    }

    useEffect(() => {
        const params = new URLSearchParams(location.search)
        const pageParam = Number(params.get('p'))
        const limitParam = Number(params.get('ps'))

        if (pageParam && pageParam > 0 && pageParam !== page) {
            setPage(pageParam)
        }

        if (limitParam && limitParam > 0 && limitParam !== limit) {
            setLimit(limitParam)
        }
    }, [location.search])

    useEffect(() => {
        if (contractAddress && selectedChain.length) {
            fetchData({ page, limit })
        }
    }, [network, page, limit])

    return (
        <div className='page'>
            <Container>
                <Col>
                    <Row>
                        <div className='page-header'>
                            <text className='page-title'>Similar Contracts Search</text>
                            <div style={{ display: 'block', marginTop: '8px' }}>
                                <text className='page-title-subcontent'>
                                    Find other contracts with similar source code.
                                </text>
                            </div>
                            <div className='page-title-border' />
                        </div>
                    </Row>
                    <div className='box table-section'>
                        <Row className='align-items-end' style={{ marginBottom: 16 }}>
                            <Col xs={12} md={6}>
                                <div style={{ marginBottom: '8px' }}>Contract Address</div>
                                <Input
                                    type='text'
                                    placeholder='0x...'
                                    className='verify-contract-input'
                                    value={contractAddress}
                                    onChange={handleAddressChange}
                                    invalid={!!error}
                                    style={{
                                        backgroundColor: 'var(--dropdown-background)',
                                        color: 'var(--white-color)',
                                        border: 'none'
                                    }}
                                    required
                                />
                            </Col>
                            <Col xs={12} md={3}>
                                <div style={{ marginBottom: '8px' }}>Chains</div>
                                <Dropdown
                                    isOpen={dropdownOpen}
                                    toggle={() => setDropdownOpen(!dropdownOpen)}
                                    className='w-100 dark-dropdown'
                                >
                                    <DropdownToggle caret className='form-dropdown-toggle'>
                                        <span
                                            style={{
                                                flex: 1,
                                                textAlign: 'left',
                                                whiteSpace: 'nowrap'
                                            }}
                                        >
                                            {selectedChain.length === 0
                                                ? 'Select'
                                                : selectedChain.length === SUPPORTED_CHAINS.length
                                                ? 'All Chains'
                                                : selectedChain.length === 1
                                                ? SUPPORTED_CHAINS.find(c => c.value === selectedChain[0])?.label
                                                : `${selectedChain.length} Chains`}
                                        </span>
                                    </DropdownToggle>
                                    <DropdownMenu
                                        className='w-100 similar-contracts-dropdown'
                                        style={{ overflowY: 'auto', maxHeight: '250px', padding: '0px' }}
                                    >
                                        <DropdownItem
                                            key='all'
                                            toggle={false}
                                            onClick={() => {
                                                if (selectedChain.length === SUPPORTED_CHAINS.length) {
                                                    setSelectedChain([])
                                                } else {
                                                    setSelectedChain(SUPPORTED_CHAINS.map(c => c.value))
                                                }
                                            }}
                                        >
                                            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                                                <Input
                                                    type='checkbox'
                                                    checked={selectedChain.length === SUPPORTED_CHAINS.length}
                                                    readOnly
                                                    className={`${network.name}-checkbox cursor-pointer`}
                                                />
                                                <span>Select All</span>
                                            </div>
                                        </DropdownItem>
                                        {SUPPORTED_CHAINS.map(chain => (
                                            <DropdownItem
                                                key={chain.value}
                                                toggle={false}
                                                onClick={() => {
                                                    if (selectedChain.includes(chain.value)) {
                                                        setSelectedChain(selectedChain.filter(c => c !== chain.value))
                                                    } else {
                                                        setSelectedChain([...selectedChain, chain.value])
                                                    }
                                                }}
                                            >
                                                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                                                    <Input
                                                        type='checkbox'
                                                        checked={selectedChain.includes(chain.value)}
                                                        readOnly
                                                        className={`${network.name}-checkbox cursor-pointer`}
                                                    />
                                                    <img src={chain.icon} width='20px' />
                                                    <span>{chain.label}</span>
                                                </div>
                                            </DropdownItem>
                                        ))}
                                    </DropdownMenu>
                                </Dropdown>
                            </Col>
                        </Row>
                        <div className='d-flex gap-3'>
                            <Button
                                onClick={() => handleSearch()}
                                style={{
                                    background: `var(--${network.name}-color)`,
                                    border: 'none',
                                    minWidth: '120px'
                                }}
                            >
                                Search
                            </Button>
                            <Button
                                onClick={() => {
                                    window.location.reload()
                                }}
                                style={{
                                    background: 'var(--dropdown-background)',
                                    border: 'none',
                                    minWidth: '120px'
                                }}
                            >
                                Reset
                            </Button>
                        </div>
                    </div>

                    {fetching && (
                        <div
                            className='box table-section'
                            style={{ paddingTop: !similarContractsData?.totalItems ? 0 : undefined }}
                        >
                            {loading ? (
                                <div
                                    className='d-flex justify-content-center align-items-center'
                                    style={{ height: '300px' }}
                                >
                                    <LoadingSpinner />
                                </div>
                            ) : (
                                <>
                                    {similarContractsData?.totalItems > 0 && (
                                        <TableHeader
                                            title={` Found a total of ${similarContractsData?.totalItems.toLocaleString()} possible match(es)`}
                                            page={page}
                                            limit={limit}
                                            totalPages={similarContractsData?.totalPages}
                                        />
                                    )}
                                    <Table
                                        responsive
                                        style={
                                            isMobile
                                                ? { display: 'block', overflowX: 'auto', whiteSpace: 'nowrap' }
                                                : {}
                                        }
                                    >
                                        <thead>
                                            <tr>
                                                <th>Chain</th>
                                                <th>Creation Tx Hash</th>
                                                <th>Similarity</th>
                                                <th>Contract Address</th>
                                                {/* <th>Disassembler</th>
                                                <th>Contract Diff</th> */}
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {similarContractsData?.totalItems === 0 ? (
                                                <tr>
                                                    <td
                                                        colSpan='7'
                                                        style={{
                                                            textAlign: 'center',
                                                            padding: '40px 0',
                                                            height: '400px'
                                                        }}
                                                    >
                                                        <div
                                                            style={{
                                                                width: '72px',
                                                                height: '72px',
                                                                backgroundColor: 'rgba(255, 193, 7, 0.15)',
                                                                borderRadius: '50%',
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                justifyContent: 'center',
                                                                margin: '0 auto 12px'
                                                            }}
                                                        >
                                                            <i
                                                                className='fas fa-inbox'
                                                                style={{
                                                                    fontSize: '24px',
                                                                    color: 'var(--grey-color)'
                                                                }}
                                                            />
                                                        </div>
                                                        <div style={{ fontWeight: 'bold', fontSize: '16px' }}>
                                                            There are no matching entries
                                                        </div>
                                                        <div style={{ color: 'var(--grey-color)', marginTop: '4px' }}>
                                                            Please try again later
                                                        </div>
                                                    </td>
                                                </tr>
                                            ) : (
                                                <>
                                                    {similarContractsData?.items.map((contract, index) => {
                                                        return (
                                                            <tr key={contract.address}>
                                                                <td>
                                                                    <img src={network.data.networkIcon} width='20px' />
                                                                    &nbsp;
                                                                    {network.data.name}
                                                                </td>
                                                                <td>
                                                                    <span
                                                                        id={`contract-transaction-hash-tooltip-${contract.address}`}
                                                                        className={`${network.name}-text-color cursor-pointer`}
                                                                        onClick={() =>
                                                                            history.push(
                                                                                `/${network.name}/tx/${contract.transactionHash}`
                                                                            )
                                                                        }
                                                                    >
                                                                        {contract.transactionHash}
                                                                    </span>
                                                                    <CopyButton
                                                                        id={`copy-transaction-hash-${contract.transactionHash}`}
                                                                        data={contract.transactionHash}
                                                                        text={'Copy Transaction Hash'}
                                                                    />
                                                                    <UncontrolledTooltip
                                                                        placement='top'
                                                                        target={`contract-transaction-hash-tooltip-${contract.address}`}
                                                                    >
                                                                        {contract.transactionHash}
                                                                    </UncontrolledTooltip>
                                                                </td>
                                                                <td>Exect</td>
                                                                <td>
                                                                    <i
                                                                        className='fas fa-file-alt'
                                                                        style={{
                                                                            fontSize: '14px',
                                                                            color: 'var(--grey-color)'
                                                                        }}
                                                                    />
                                                                    &nbsp;
                                                                    <span
                                                                        id={`contract-address-tooltip-${contract.address}`}
                                                                        className={`${network.name}-text-color cursor-pointer`}
                                                                        onClick={() =>
                                                                            history.push(
                                                                                `/${network.name}/address/${contract.address}`
                                                                            )
                                                                        }
                                                                    >
                                                                        {contract.address}
                                                                    </span>
                                                                    <CopyButton
                                                                        id={`copy-address-${contract.address}`}
                                                                        data={contract.address}
                                                                        text={'Copy Address'}
                                                                    />
                                                                    <UncontrolledTooltip
                                                                        placement='top'
                                                                        target={`contract-address-tooltip-${contract.address}`}
                                                                    >
                                                                        {contract.address}
                                                                    </UncontrolledTooltip>
                                                                </td>
                                                            </tr>
                                                        )
                                                    })}
                                                </>
                                            )}
                                        </tbody>
                                    </Table>
                                    {similarContractsData?.totalItems > 0 && (
                                        <TableFooter
                                            limit={limit}
                                            setLimit={setLimit}
                                            page={page}
                                            setPage={setPage}
                                            totalPages={similarContractsData?.totalPages}
                                        />
                                    )}
                                </>
                            )}
                        </div>
                    )}
                </Col>
            </Container>
        </div>
    )
}

module.exports = SimilarContracts
